import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useTranslation } from 'react-i18next';
import { batchesAPI, tutorsAPI, studentsAPI, paymentsAPI, settingsAPI, merchandiseAPI, resolveImage, uploadAPI , translationAPI} from '../utils/api';
import OrganizationPaymentDetailsWidget from '../components/OrganizationPaymentDetailsWidget';
import { tField } from '../utils/bilingualHelper';

const parse12h = (str) => {
  if (!str) return { h: '09', m: '00', p: 'AM' };
  
  // Handle both "09:00 AM" and "17:30"
  const parts = str.split(' ');
  const timePart = parts[0];
  let period = parts[1];

  let [h, m] = timePart.split(':');
  let hour = parseInt(h, 10);
  let min = m || '00';

  if (!period) {
    // Detect 24h
    if (hour >= 12) {
      period = 'PM';
      if (hour > 12) hour -= 12;
    } else {
      period = 'AM';
      if (hour === 0) hour = 12;
    }
  }

  return { 
    h: hour.toString().padStart(2, '0'), 
    m: min.toString().padStart(2, '0'), 
    p: period.toUpperCase() 
  };
};

const format12h = (h, m, p) => `${h}:${m} ${p}`;
const HOURS = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
const MINS  = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));

function EditBatchModal({ batch, onClose, onUpdated }) {

  const startObj = parse12h(batch.startTime);
  const endObj   = parse12h(batch.endTime);

  const [form, setForm] = useState({ 
    ...batch, 
    name: typeof batch.name === 'string' ? { en: batch.name, zh: '' } : (batch.name || { en: '', zh: '' }),
    category: typeof batch.category === 'string' ? { en: batch.category, zh: '' } : (batch.category || { en: '', zh: '' }),
    shortDesc: typeof batch.shortDesc === 'string' ? { en: batch.shortDesc, zh: '' } : (batch.shortDesc || { en: '', zh: '' }),
    tutor: batch.tutor?.id || batch.tutor?._id || batch.tutor,
    startDate: batch.startDate || '',
    endDate: batch.endDate || '',
    termDiscountPercent: batch.termDiscountPercent !== undefined ? batch.termDiscountPercent : 20,
    isAdvertised: batch.isAdvertised !== undefined ? batch.isAdvertised : true,
    isPopular: batch.isPopular !== undefined ? batch.isPopular : false,
    offeredMerchandise: batch.offeredMerchandise?.map(m => m.id || m._id || m) || []
  });
  const [startTime, setStartTime] = useState(startObj);
  const [endTime, setEndTime]     = useState(endObj);
  const [tutors, setTutors] = useState([]);
  const [masterLevels, setMasterLevels] = useState([]); // Dynamic Levels
  const [allMerchandise, setAllMerchandise] = useState([]);
  const [loading, setLoad]  = useState(false);
  const [error, setError]   = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(batch?.coverImage ? resolveImage(batch.coverImage) : '');

  const [translating, setTranslating] = useState(false);
  const [translated, setTranslated] = useState(
    !!(batch?.name?.zh || batch?.category?.zh || batch?.shortDesc?.zh)
  );

  const handleTranslateAll = async () => {
    if (!form.name?.en || !form.category?.en || !form.shortDesc?.en) {
      alert("Please fill in Batch Name, Category, and Short Description in English first.");
      return;
    }
    setTranslating(true);
    try {
      const texts = [form.name.en, form.category.en, form.shortDesc.en];
      const translatedTexts = [];
      for (const text of texts) {
        const data = await translationAPI.translate(text, 'en', 'zh-TW');
        
        translatedTexts.push(data.responseData?.translatedText || '');
        await new Promise(r => setTimeout(r, 300));
      }
      setForm(prev => ({
        ...prev,
        name: { ...prev.name, zh: translatedTexts[0] },
        category: { ...prev.category, zh: translatedTexts[1] },
        shortDesc: { ...prev.shortDesc, zh: translatedTexts[2] }
      }));
      setTranslated(true);
    } catch (err) {
      console.error(err);
      alert('Translation failed. Please try again or fill manually.');
    } finally {
      setTranslating(false);
    }
  };

  useEffect(() => {
    // Fetch Tutors and Academy Settings (Levels)
    const loadData = async () => {
      try {
        const [tRes, sRes, mRes] = await Promise.all([
          tutorsAPI.getAll(),
          settingsAPI.get(),
          merchandiseAPI.getAll()
        ]);
        setTutors(tRes.data);
        setMasterLevels(sRes.data.masterLevels || ['Beginner', 'Intermediate', 'Advanced']);
        setAllMerchandise(mRes.data.items || []);
      } catch (err) { console.error('Failed to load Modal Data:', err); }
    };
    loadData();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!form.name?.en?.trim()) return setError('Batch name (English) is required.');
    if (!form.category?.en?.trim()) return setError('Category / Tag (English) is required.');
    if (!form.shortDesc?.en?.trim()) return setError('Short description (English) is required.');
    if (!translated || !form.name?.zh || !form.category?.zh || !form.shortDesc?.zh) {
      return setError('Please click "Translate All" to generate Chinese translations before saving.');
    }
    setLoad(true); setError('');
    let finalCoverImage = form.coverImage;
    try {
      if (imageFile) {
        const formData = new FormData();
        formData.append('photo', imageFile);
        const upRes = await uploadAPI.batchImage(formData);
        finalCoverImage = upRes.data.url;
      }
      const payload = {
        ...form,
        coverImage: finalCoverImage,
        startTime: format12h(startTime.h, startTime.m, startTime.p),
        endTime: format12h(endTime.h, endTime.m, endTime.p)
      };
      const res = await batchesAPI.update(batch._id || batch.id, payload);
      onUpdated(res.data.batch || res.data);
      onClose();
    } catch(err) {
      setError(err.response?.data?.message || 'Failed to update batch.');
    } finally { setLoad(false); }
  };

  const toggleDay = (day) => {
    setForm(p => ({
      ...p,
      days: p.days.includes(day) ? p.days.filter(d => d !== day) : [...p.days, day]
    }));
  };

  const toggleMerchandise = (mId) => {
    setForm(p => {
      const current = p.offeredMerchandise || [];
      return {
        ...p,
        offeredMerchandise: current.includes(mId) ? current.filter(id => id !== mId) : [...current, mId]
      };
    });
  };


  return (
    <div className="dash-modal-overlay">
      <div className="dash-modal" style={{ maxWidth: 520, borderRadius: 24, overflow: 'auto' }}>
        <div className="dash-modal-header" style={{ background: 'var(--wine)', padding: '24px 30px' }}>
          <div className="dash-modal-title" style={{ color: '#fff', fontSize: 22 }}>Batch Configuration</div>
          <button className="dash-modal-close" onClick={onClose} style={{ color: '#fff' }}>✕</button>
        </div>
        <div className="dash-modal-body" style={{ padding: '30px' }}>
          {error && <div className="form-status-box error mb-4">{error}</div>}
          
          <div className="form-group">
            <label className="form-label-style"><span style={{ marginRight: 8 }}>🏷️</span> Batch Identity <span style={{color:'#888',fontWeight:400,fontSize:11}}>(English)</span></label>
            <input 
              className="form-input-style" 
              placeholder="e.g. Morning Hip Hop A" 
              value={form.name?.en || ''} 
              onChange={e => { setForm(p=>({...p,name:{...p.name, en:e.target.value}})); setTranslated(false); }} 
              style={{ fontSize: 16, fontWeight: 600 }}
            />
          </div>

          <div className="form-group">
            <label className="form-label-style">Category / Tag <span style={{color:'#888',fontWeight:400,fontSize:11}}>(English)</span></label>
            <input className="form-input-style" value={form.category?.en || ''} onChange={e => { setForm(p=>({...p,category:{...p.category, en:e.target.value}})); setTranslated(false); }} />
          </div>
          
          <div className="form-group">
            <label className="form-label-style">Short Description <span style={{color:'#888',fontWeight:400,fontSize:11}}>(English)</span></label>
            <textarea className="form-input-style" rows={2} value={form.shortDesc?.en || ''} onChange={e => { setForm(p=>({...p,shortDesc:{...p.shortDesc, en:e.target.value}})); setTranslated(false); }} />
          </div>

          {/* ── One-click Translate All button ── */}
          <div style={{ background: translated ? 'rgba(76,175,134,0.08)' : 'rgba(123,29,58,0.06)', border: `1px solid ${translated ? '#4caf86' : 'rgba(123,29,58,0.2)'}`, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ flex: 1 }}>
              {translated ? (
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#4caf86', marginBottom: 4 }}>✅ Chinese Translation Ready</div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    <span style={{ marginRight: 16 }}>名稱: <b>{form.name?.zh || '—'}</b></span>
                    <span style={{ marginRight: 16 }}>類別: <b>{form.category?.zh || '—'}</b></span>
                  </div>
                  <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>描述: {form.shortDesc?.zh || '—'}</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#7b1d3a', marginBottom: 2 }}>🌐 Chinese Translation Required</div>
                  <div style={{ fontSize: 12, color: '#888' }}>Fill all English fields above, then click to auto-translate.</div>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleTranslateAll}
              disabled={translating}
              style={{ background: translated ? '#4caf86' : 'var(--wine)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontWeight: 700, fontSize: 13, cursor: translating ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', opacity: translating ? 0.7 : 1, minWidth: 120 }}
            >
              {translating ? '⏳ Translating...' : translated ? '🔄 Re-translate' : '✨ Translate All'}
            </button>
          </div>

          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label-style"><span style={{ marginRight: 8 }}>💎</span> Academic Level</label>
              <select 
                 className="form-input-style academy-level-select" 
                 value={form.level} 
                 onChange={e => setForm(p=>({...p,level:e.target.value}))}
              >
                {masterLevels.map(lvl => <option key={lvl} value={lvl}>{lvl}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label-style"><span style={{ marginRight: 8 }}>👥</span> Capacity</label>
              <input 
                className="form-input-style" 
                type="number" 
                value={form.maxStudents} 
                onChange={e => setForm(p=>({...p,maxStudents:Number(e.target.value)}))} 
                style={{ fontWeight: 700 }}
              />
            </div>
            <div className="form-group">
              <label className="form-label-style"><span style={{ marginRight: 8 }}>💰</span> Batch Fee (HK$)</label>
              <input 
                className="form-input-style" 
                type="number" 
                value={form.feeAmount} 
                onChange={e => setForm(p=>({...p,feeAmount:Number(e.target.value)}))} 
                style={{ fontWeight: 700 }}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label-style"><span style={{ marginRight: 8 }}>🎓</span> Lead Tutor / Instructor</label>
            <select className="form-input-style" value={form.tutor} onChange={e => setForm(p=>({...p,tutor:e.target.value}))}>
              <option value="">Select an Instructor</option>
              {tutors.map(tutor => <option key={tutor._id || tutor.id} value={tutor._id || tutor.id}>{tField(tutor.name)} — {tutor.specialization?.[0] || 'Faculty'}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label-style">Cover Image</label>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <div style={{ width: 80, height: 60, borderRadius: 4, background: '#eee', backgroundImage: `url(${imagePreview})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
              <input type="file" accept="image/*" onChange={handleImageChange} />
            </div>
          </div>
          
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, marginBottom: 0 }}>
            <input 
              type="checkbox" 
              id="isAdvertisedDetail" 
              style={{ width: 18, height: 18, cursor: 'pointer', accentColor: 'var(--wine)' }} 
              checked={!!form.isAdvertised} 
              onChange={e => setForm(p=>({...p,isAdvertised: e.target.checked}))} 
            />
            <label htmlFor="isAdvertisedDetail" style={{ fontSize: 13, fontWeight: 600, color: '#333', cursor: 'pointer', userSelect: 'none' }}>
              📢 Advertise this class on the public Landing Page pricing section
            </label>
          </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, marginBottom: 0 }}>
              <input 
                type="checkbox" 
                id="isPopularDetail" 
                style={{ width: 18, height: 18, cursor: 'pointer', accentColor: 'var(--wine)' }} 
                checked={!!form.isPopular} 
                onChange={e => setForm(p=>({...p,isPopular: e.target.checked}))} 
              />
              <label htmlFor="isPopularDetail" style={{ fontSize: 13, fontWeight: 600, color: '#333', cursor: 'pointer', userSelect: 'none' }}>
                ✨ Highlight this class as "Best Seller" (Premium Black Card)
              </label>
            </div>
            <div className="form-row-2" style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              <div className="form-group mb-0">
                <label className="form-label-style">Start Date</label>
                <input type="date" className="form-input-style" value={form.startDate || ''} onChange={e => setForm(p=>({...p,startDate:e.target.value}))} />
              </div>
              <div className="form-group mb-0">
                <label className="form-label-style">End Date</label>
                <input type="date" className="form-input-style" value={form.endDate || ''} onChange={e => setForm(p=>({...p,endDate:e.target.value}))} />
              </div>
              <div className="form-group mb-0">
                <label className="form-label-style">Term Discount (%)</label>
                <input type="number" className="form-input-style" placeholder="20" min="0" max="100" value={form.termDiscountPercent !== undefined ? form.termDiscountPercent : 20} onChange={e => setForm(p=>({...p,termDiscountPercent:Number(e.target.value)}))} />
              </div>
            </div>
            
            <div className="form-group" style={{ marginTop: 16, marginBottom: 0 }}>
              <label className="form-label-style">Complimentary Merchandise Offered</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                {allMerchandise.map(m => {
                  const mId = m.id || m._id;
                  const isSelected = (form.offeredMerchandise || []).includes(mId);
                  return (
                    <div 
                      key={mId} 
                      className={`badge ${isSelected ? 'badge-blue' : ''}`} 
                      style={{ cursor:'pointer', border:'1px solid #ccc', background: isSelected ? undefined : '#f9f9f9', color: isSelected ? undefined : '#666' }} 
                      onClick={() => toggleMerchandise(mId)}
                    >
                      {tField(m.name)}
                    </div>
                  );
                })}
              </div>
              {allMerchandise.length === 0 && <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>No merchandise items found.</div>}
            </div>

          <div className="form-group">
            <label className="form-label-style"><span style={{ marginRight: 8 }}>📅</span> Schedule Days</label>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop: 8 }}>
              {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
                <button 
                  key={d} 
                  type="button"
                  className={`badge ${form.days?.includes(d) ? 'badge-gold' : 'badge-muted'}`} 
                  style={{ cursor:'pointer', border:'none', padding: '8px 12px', fontSize: 11 }} 
                  onClick={() => toggleDay(d)}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>


          <div className="form-row-2" style={{ marginTop: 10 }}>
            {/* Start Time */}
            <div className="form-group">
              <label className="form-label-style"><span style={{ marginRight: 8 }}>🕙</span> Start Time</label>
              <div className="time-picker-container">
                <div className="time-select-wrapper">
                  <select className="time-unit-select" value={startTime.h} onChange={e => setStartTime(p=>({...p,h:e.target.value}))}>
                    {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                  <span className="time-colon">:</span>
                  <select className="time-unit-select" value={startTime.m} onChange={e => setStartTime(p=>({...p,m:e.target.value}))}>
                    {MINS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="ampm-toggle-pill">
                  <button type="button" className={`ampm-btn ${startTime.p === 'AM' ? 'active' : ''}`} onClick={() => setStartTime(p=>({...p,p:'AM'}))}>AM</button>
                  <button type="button" className={`ampm-btn ${startTime.p === 'PM' ? 'active' : ''}`} onClick={() => setStartTime(p=>({...p,p:'PM'}))}>PM</button>
                </div>
              </div>
            </div>

            {/* End Time */}
            <div className="form-group">
              <label className="form-label-style"><span style={{ marginRight: 8 }}>🕚</span> End Time</label>
              <div className="time-picker-container">
                <div className="time-select-wrapper">
                  <select className="time-unit-select" value={endTime.h} onChange={e => setEndTime(p=>({...p,h:e.target.value}))}>
                    {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                  <span className="time-colon">:</span>
                  <select className="time-unit-select" value={endTime.m} onChange={e => setEndTime(p=>({...p,m:e.target.value}))}>
                    {MINS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="ampm-toggle-pill">
                  <button type="button" className={`ampm-btn ${endTime.p === 'AM' ? 'active' : ''}`} onClick={() => setEndTime(p=>({...p,p:'AM'}))}>AM</button>
                  <button type="button" className={`ampm-btn ${endTime.p === 'PM' ? 'active' : ''}`} onClick={() => setEndTime(p=>({...p,p:'PM'}))}>PM</button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="dash-modal-footer" style={{ padding: '24px 30px', background: '#fcfcfc' }}>
          <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={loading} style={{ padding: '10px 24px' }}>
            {loading ? 'Processing...' : 'Save Configuration'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddStudentModal({ batchObj, onAdded, onClose }) {
  const { t } = useTranslation();
  const [students, setStudents] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  
  const [step, setStep] = useState(1);
  const [selectedStudent, setSelectedStudent] = useState(null);
  
  // Merchandise State
  const [selectedMerchandise, setSelectedMerchandise] = useState([]);
  const [sizePromptData, setSizePromptData] = useState(null);

  const getBatchMonthsForReg = (batch) => {
    if (!batch) return [];
    const CURRENT = new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    if (!batch.startDate && !batch.endDate) return [CURRENT];
    const startD = batch.startDate ? new Date(batch.startDate) : new Date();
    const endD = batch.endDate
      ? new Date(batch.endDate)
      : (() => { const d = new Date(startD); d.setFullYear(d.getFullYear() + 1); return d; })();
    const list = [];
    const d = new Date(startD);
    d.setDate(1);
    let count = 0;
    while (d <= endD && count < 36) {
      list.push(d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }));
      d.setMonth(d.getMonth() + 1);
      count++;
    }
    return list.length > 0 ? list : [CURRENT];
  };

  // Fee State
  const [selectedFeeMonths, setSelectedFeeMonths] = useState([]);
  const [feeMethod, setFeeMethod] = useState('Cash');
  const [receivedFrom, setReceivedFrom] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptUrl, setReceiptUrl] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [declarationSigned, setDeclarationSigned] = useState(false);

  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReceiptFile(file);
    const formData = new FormData();
    formData.append('receipt', file);
    setUploadingReceipt(true);
    try {
      const res = await uploadAPI.paymentReceipt(formData);
      setReceiptUrl(res.data.url);
    } catch (err) {
      alert('Receipt upload failed. Please try again.');
      setReceiptFile(null);
    } finally {
      setUploadingReceipt(false);
    }
  };

  useEffect(() => {
    const CURRENT_MONTH_STR = new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    const months = getBatchMonthsForReg(batchObj);
    setSelectedFeeMonths(months.includes(CURRENT_MONTH_STR) ? [CURRENT_MONTH_STR] : months.length > 0 ? [months[0]] : []);
  }, [batchObj]);

  const batchMonthsForReg = getBatchMonthsForReg(batchObj);
  const CURRENT_MONTH_STR = new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  const toggleFeeMonth = (month) => {
    setSelectedFeeMonths(prev =>
      prev.includes(month) ? prev.filter(m => m !== month) : [...prev, month]
    );
  };

  useEffect(() => {
    studentsAPI.getAll()
      .then(res => {
        setStudents(res.data.filter(s => {
          const inBatch = 
            (s.batch === batchObj._id || s.batch?._id === batchObj._id) || 
            (s.batchIds && s.batchIds.some(bid => bid === batchObj._id || bid?._id === batchObj._id));
          return s.currentLevel === batchObj.level && !inBatch;
        }));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [batchObj._id, batchObj.level]);

  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
    setStep(2);
  };

  const toggleMerchandise = (m) => {
    const mId = m.id || m._id;
    const isSelected = selectedMerchandise.some(item => (item.id || item._id) === mId);
    if (isSelected) {
      setSelectedMerchandise(prev => prev.filter(item => (item.id || item._id) !== mId));
    } else {
      if (m.sizes && m.sizes.length > 0) {
        setSizePromptData({ merchandise: m });
      } else {
        setSelectedMerchandise(prev => [...prev, m]);
      }
    }
  };

  const confirmMerchandiseSize = (size) => {
    if (sizePromptData) {
      setSelectedMerchandise(prev => [...prev, { ...sizePromptData.merchandise, selectedSize: size }]);
      setSizePromptData(null);
    }
  };

  const handleEnroll = async () => {
    setError('');
    if (!selectedStudent || !selectedStudent._id) {
      setError('Please select a student to enroll.');
      return;
    }
    const missingSize = selectedMerchandise.find(m => m.sizes && m.sizes.length > 0 && !m.selectedSize);
    if (missingSize) {
      setError(`Please select a size for merchandise: ${missingSize.name}.`);
      return;
    }
    if (!declarationSigned) {
      setError('Please accept the enrollment declaration before proceeding.');
      return;
    }
    const totalMerchPrice = selectedMerchandise.reduce((acc, m) => acc + (m.price || 0), 0);
    const totalFee = (batchObj.feeAmount * selectedFeeMonths.length) + totalMerchPrice;
    if (totalFee > 0 && !receivedFrom.trim() && !selectedStudent?.parent?.name) {
      setError('Please enter who the payment was received from.');
      return;
    }
    if (totalFee > 0 && feeMethod === 'Bank/QR Transfer') {
      if (!receiptUrl && !receiptFile) {
        setError('Please upload a payment receipt for Bank/QR Transfer.');
        return;
      }
      if (uploadingReceipt) {
        setError('Please wait for the payment receipt to finish uploading.');
        return;
      }
    }

    setProcessing(true);
    try {
      // 1. Enroll
      await batchesAPI.addStudent(batchObj._id, { existingStudentId: selectedStudent._id });
      
      // 2. Merchandise Updates
      if (selectedMerchandise.length > 0) {
        const currentMerch = selectedStudent.purchasedMerchandise || [];
        const newMerchPayload = selectedMerchandise.map(m => ({
           merchandise: m.id || m._id,
           priceAtPurchase: m.price || 0,
           size: m.selectedSize || null
        }));
        await studentsAPI.update(selectedStudent._id, { purchasedMerchandise: [...currentMerch, ...newMerchPayload] });
      }

      // 3. Payment Submission
      const totalMerchPrice = selectedMerchandise.reduce((acc, m) => acc + (m.price || 0), 0);
      const totalFee = (batchObj.feeAmount * selectedFeeMonths.length) + totalMerchPrice;

      if (totalFee > 0) {
        await paymentsAPI.submitMultiple({
          studentId: selectedStudent.studentId,
          batchId: batchObj._id,
          amountPerMonth: batchObj.feeAmount,
          months: selectedFeeMonths,
          merchandiseFee: totalMerchPrice,
          paymentMethod: feeMethod === 'Bank/QR Transfer' ? 'QR/Transfer' : feeMethod,
          paidBy: 'Parent/Guardian',
          guardianName: receivedFrom || selectedStudent?.parent?.name || 'Admin Entry',
          receiptUrl: receiptUrl || null
        });
      }

      onAdded();
      onClose();
    } catch (err) {
      alert(err.response?.data?.message || 'Enrollment checkout failed');
    } finally {
      setProcessing(false);
    }
  };

  const filtered = students.filter(s => 
    tField(s.name).toLowerCase().includes(search.toLowerCase()) || 
    s.studentId?.toLowerCase().includes(search.toLowerCase())
  ).sort((a,b) => {
    const aMatch = a.preferredBatch === batchObj.name ? 1 : 0;
    const bMatch = b.preferredBatch === batchObj.name ? 1 : 0;
    return bMatch - aMatch;
  });

  const totalMerchandisePrice = selectedMerchandise.reduce((sum, m) => sum + (m.price || 0), 0);
  const totalFeeAmount = (batchObj.feeAmount * selectedFeeMonths.length) + totalMerchandisePrice;

  return (
    <div className="dash-modal-overlay">
      <div className="dash-modal" style={{ maxWidth: 640 }}>
        <div className="dash-modal-header" style={{ background: 'linear-gradient(135deg, #7b1d3a, #c04060)' }}>
          <div className="dash-modal-title" style={{ color:'#fff' }}>{t('recruitCheckout.title', 'Recruit Student Checkout')}</div>
          <button className="dash-modal-close" onClick={onClose} style={{ color:'#fff' }}>✕</button>
        </div>

        <div className="dash-modal-body" style={{ padding: '20px' }}>
          {step === 1 ? (
             // Step 1 UI
             <>
               <div style={{ marginBottom: 16 }}>
                 <p className="text-sm text-muted">{t('recruitCheckout.selectCandidateFor', 'Select an eligible candidate for')} <strong>{tField(batchObj.name)}</strong> ({batchObj.level})</p>
               </div>
               <div className="dash-card mb-4" style={{ padding:'0 16px', borderRadius:8, background:'#f8f9fa' }}>
                 <input 
                   className="form-input-style" 
                   placeholder={t('recruitCheckout.searchPlaceholder', '🔍 Search eligible students by name or ID...')} 
                   value={search}
                   onChange={e => setSearch(e.target.value)}
                   style={{ border:'none', background:'transparent', boxShadow:'none' }}
                 />
               </div>
               {loading ? (
                 <div style={{ textAlign: 'center', padding: '40px 0', color: '#7a7068' }}>{t('recruitCheckout.matchingCandidates', '⏳ Matching candidates...')}</div>
               ) : filtered.length === 0 ? (
                 <div style={{ textAlign: 'center', padding: '40px 0' }}>
                   <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
                   <div style={{ fontWeight: 600, color: '#111' }}>{t('recruitCheckout.noCandidatesFound', 'No eligible candidates found')}</div>
                 </div>
               ) : (
                 <div className="candidate-list" style={{ maxHeight: 400, overflowY: 'auto' }}>
                   {filtered.map(s => (
                     <div key={s._id} style={{ 
                       display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
                       padding: '16px', border: '1px solid #f0ece8', borderRadius: 12, marginBottom: 10,
                       background: s.preferredBatch === batchObj.name ? '#fff9fa' : '#fff',
                       borderColor: s.preferredBatch === batchObj.name ? '#f0e0e8' : '#f0ece8'
                     }}>
                         <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                           <div style={{ 
                             width: 42, height: 42, borderRadius: '50%', backgroundColor: '#7b1d3a', 
                             backgroundImage: s.photoUrl ? `url("${resolveImage(s.photoUrl)}")` : 'none',
                             backgroundSize: 'cover', backgroundPosition: 'center',
                             color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 
                           }}>
                             {!s.photoUrl && s.name?.[0]}
                           </div>
                         <div>
                           <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                             <span style={{ fontWeight: 700, color: '#111' }}>{tField(s.name)}</span>
                           </div>
                           <div style={{ fontSize: 12, color: '#7a7068' }}>{s.studentId} · Level: {s.currentLevel}</div>
                         </div>
                       </div>
                       <button className="btn btn-primary btn-sm" onClick={() => handleSelectStudent(s)}>
                         {t('recruitCheckout.selectStudentBtn', 'Select Student')}
                       </button>
                     </div>
                   ))}
                 </div>
               )}
             </>
          ) : (
             // Step 2 UI (Checkout) 
             <>
               <div style={{ display: 'flex', alignItems: 'center', gap: 14, paddingBottom: 16, borderBottom: '1px solid #eee', marginBottom: 20 }}>
                 <button className="btn btn-sm btn-outline" onClick={() => setStep(1)}>{t('batchDetail.back')}</button>
                 <div>
                   <div style={{ fontSize: 13, color: '#666' }}>{t('recruitCheckout.enrolling', 'Enrolling:')}</div>
                   <div style={{ fontWeight: 700 }}>{tField(selectedStudent.name)} <span style={{ color: '#888', fontWeight: 400 }}>#{selectedStudent.studentId}</span></div>
                 </div>
               </div>

               {batchObj.offeredMerchandise && batchObj.offeredMerchandise.length > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: '#666', marginBottom: 12 }}>
                      {t('recruitCheckout.complimentaryMerchandise', 'Complimentary Merchandise')}
                    </div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      {batchObj.offeredMerchandise.filter(m => m).map(m => {
                        const mId = m.id || m._id;
                        const selectedItem = selectedMerchandise.find(item => (item.id || item._id) === mId);
                        const isSelected = !!selectedItem;
                        return (
                          <div
                            key={mId}
                            onClick={() => toggleMerchandise(m)}
                            style={{
                              padding: '10px 16px', borderRadius: 8, border: `2px solid ${isSelected ? 'var(--wine)' : '#eee'}`,
                              background: isSelected ? '#fffafa' : '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8
                            }}
                          >
                            <div style={{ 
                              width: 18, height: 18, borderRadius: 4, 
                              background: isSelected ? 'var(--wine)' : '#fff', border: isSelected ? 'none' : '2px solid #ccc',
                              display: 'flex', alignItems: 'center', justifyContent: 'center' 
                            }}>
                              {isSelected && <span style={{ color: '#fff', fontSize: 12 }}>✓</span>}
                            </div>
                            {m.imageUrl && (
                              <img 
                                src={resolveImage(m.imageUrl)} 
                                alt={tField(m.name)} 
                                style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'cover' }} 
                              />
                            )}
                            <span style={{ fontSize: 13, fontWeight: 600, color: isSelected ? 'var(--wine)' : '#444' }}>
                              {tField(m.name)} {isSelected && selectedItem.selectedSize ? `(Size: ${selectedItem.selectedSize}) ` : ''}(+HK$ {m.price})
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Declaration Section */}
                <div style={{ marginBottom: 24, padding: '16px', background: '#fdfdfd', border: '1px solid #eee', borderRadius: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', margin: 0 }}>
                    <input 
                      type="checkbox" 
                      checked={declarationSigned}
                      onChange={e => { setDeclarationSigned(e.target.checked); setError(''); }}
                      style={{ marginTop: 4, width: 18, height: 18, accentColor: 'var(--wine)' }}
                    />
                    <div style={{ fontSize: 13, color: '#555', lineHeight: '1.5' }}>
                      <strong style={{ color: '#111', display: 'block', marginBottom: 4 }}>{t('recruitCheckout.enrollmentDeclaration', 'Enrollment Declaration')} <span style={{ color: 'red' }}>*</span></strong>
                      {t('recruitCheckout.declarationText', "I confirm that all information provided is accurate and I have read and agree to the academy's terms and conditions on behalf of the student.")}
                    </div>
                  </label>
                </div>

                <div style={{ background: '#fff9fa', border: '1px solid #f0e0e8', borderRadius: 12, padding: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                    <span style={{ fontSize: 18 }}>💎</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--black)' }}>{t('recruitCheckout.recordFeePayment', 'Record Fee Payment')}</div>
                      <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{t('recruitCheckout.recordFeePaymentSub', 'Optional - select months if fee is collected now.')} HK$ {batchObj.feeAmount.toLocaleString()} / month</div>
                    </div>
                  </div>
                  
                  {/* Month checkboxes */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 8,
                    maxHeight: 220,
                    overflowY: 'auto',
                    marginBottom: 16
                  }}>
                    {batchMonthsForReg.map(month => {
                      const checked = selectedFeeMonths.includes(month);
                      const isCurrent = month === CURRENT_MONTH_STR;
                      return (
                        <label
                          key={month}
                          onClick={() => toggleFeeMonth(month)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '8px 12px',
                            borderRadius: 8,
                            border: `2px solid ${checked ? 'var(--wine, #8B1D3B)' : '#e5e5e5'}`,
                            background: checked ? 'rgba(139,29,59,0.06)' : '#fff',
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: checked ? 700 : 400,
                            color: checked ? 'var(--wine, #8B1D3B)' : '#555',
                            transition: 'all 0.15s',
                            userSelect: 'none',
                          }}
                        >
                          <div style={{
                            width: 16, height: 16, borderRadius: '50%',
                            border: `2px solid ${checked ? 'var(--wine, #8B1D3B)' : '#ccc'}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: checked ? 'var(--wine, #8B1D3B)' : '#fff'
                          }}>
                            {checked && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                          </div>
                          <span>{month} {isCurrent && <span style={{ fontSize: 10, color: '#aaa', marginLeft: 4 }}>({t('recruitCheckout.currentMonth', 'Current')})</span>}</span>
                        </label>
                      );
                    })}
                  </div>

                  <div className="form-row-2">
                    <div className="form-group mb-0">
                      <label className="form-label-style">{t('recruitCheckout.paymentMethod', 'Payment Method')}</label>
                      <select className="form-input-style" value={feeMethod} onChange={e => setFeeMethod(e.target.value)}>
                        <option value="Cash">{t('recruitCheckout.cash', 'Cash')}</option>
                        <option value="Bank/QR Transfer">{t('recruitCheckout.bankTransfer', 'Bank / QR Transfer')}</option>
                      </select>
                    </div>
                    <div className="form-group mb-0">
                      <label className="form-label-style">{t('recruitCheckout.receivedFrom', 'Received From')}</label>
                      <input 
                        type="text" 
                        className="form-input-style" 
                        placeholder={tField(selectedStudent?.parent?.name) || t('recruitCheckout.guardianNamePlaceholder', 'Guardian name...')}
                        value={receivedFrom}
                        onChange={e => { setError(''); setReceivedFrom(e.target.value); }}
                      />
                    </div>
                  </div>

                  {feeMethod === 'Bank/QR Transfer' && (
                    <div style={{ marginTop: '16px' }}>
                      <OrganizationPaymentDetailsWidget containerStyle={{ margin: 0, boxShadow: 'none', border: '1px solid #eaeaea' }} />
                      <div className="form-group" style={{ marginTop: 16 }}>
                        <label className="form-label-style">{t('recruitCheckout.uploadPaymentReceipt', 'UPLOAD PAYMENT RECEIPT')} <span style={{ color: 'red' }}>*</span></label>
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          className="form-input-style"
                          style={{ padding: '8px', background: '#fff' }}
                          onChange={handleReceiptUpload}
                          disabled={uploadingReceipt}
                        />
                        {uploadingReceipt && (
                          <div style={{ fontSize: 12, color: '#d97706', marginTop: 4, fontWeight: 600 }}>
                            {t('recruitCheckout.uploadingReceipt', '⏳ Uploading receipt...')}
                          </div>
                        )}
                        {receiptFile && !uploadingReceipt && (
                          <div style={{ fontSize: 12, color: '#2d7a52', marginTop: 4, fontWeight: 600 }}>
                            ✅ {receiptFile.name} {t('recruitCheckout.attached', 'attached')}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px dashed #f0e0e8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 12, color: '#666' }}>{t('recruitCheckout.batchFee', 'Batch Fee: HK$')} {batchObj.feeAmount * selectedFeeMonths.length}</div>
                      {totalMerchandisePrice > 0 && <div style={{ fontSize: 12, color: '#666' }}>{t('recruitCheckout.merchandiseFee', 'Merchandise: HK$')} {totalMerchandisePrice}</div>}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>{t('recruitCheckout.totalFeeAmount', 'Total Fee Amount')}</div>
                      <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--wine)' }}>HK$ {totalFeeAmount}</div>
                    </div>
                  </div>
                </div>

                {error && (
                  <div style={{ color: 'var(--wine)', fontSize: 13, fontWeight: 600, marginTop: 16, textAlign: 'right' }} className="animate-fade-in">
                    ⚠️ {error}
                  </div>
                )}
                <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="btn btn-primary" onClick={handleEnroll} disabled={processing}>
                    {processing ? t('recruitCheckout.processing', 'Processing...') : t('recruitCheckout.authorizeBtn', 'Authorize Enrollment & Payment')}
                  </button>
                </div>
             </>
          )}
        </div>

        {sizePromptData && (
          <div className="dash-modal-overlay" style={{ zIndex: 1100, background: 'rgba(0,0,0,0.4)' }}>
            <div className="dash-modal" style={{ maxWidth: 320, padding: 24, textAlign: 'center' }}>
              <h3 style={{ marginTop: 0, color: 'var(--wine)' }}>Select Size</h3>
              <p className="text-sm text-muted">Please select a size for {tField(sizePromptData.merchandise.name)}</p>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', marginTop: 20 }}>
                {sizePromptData.merchandise.sizes.map((size, idx) => (
                  <button 
                    key={typeof size === 'object' ? (size.en || size.zh || idx) : size}
                    className="btn btn-outline" 
                    onClick={() => confirmMerchandiseSize(typeof size === 'object' ? tField(size) : size)}
                    style={{ minWidth: 60 }}
                  >
                    {typeof size === 'object' ? tField(size) : size}
                  </button>
                ))}
              </div>
              <button className="btn btn-outline btn-sm" style={{ marginTop: 24, width: '100%' }} onClick={() => setSizePromptData(null)}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ClassroomQrModal({ batch, onClose }) {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'zh' ? 'zh' : 'en';
  const [copied, setCopied] = useState(false);

  const batchId = batch._id || batch.id;
  const batchNameStr = typeof batch.name === 'object' 
    ? (batch.name.en || batch.name.zh || 'Batch') 
    : (batch.name || 'Batch');
  
  const qrPayload = JSON.stringify({
    type: 'batch_checkin',
    batchId: batchId,
    batchName: batchNameStr,
  });

  const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=260x260&margin=8&data=${encodeURIComponent(qrPayload)}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(qrPayload);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="dash-modal-overlay" onClick={onClose} style={{ zIndex: 9999 }}>
      <div 
        className="dash-modal" 
        onClick={e => e.stopPropagation()} 
        style={{ maxWidth: 480, width: '92%', borderRadius: 16, overflow: 'hidden', padding: 0 }}
      >
        {/* Printable Card Area */}
        <div id="printable-classroom-card" style={{ padding: '28px 24px', background: '#fff', textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 20 }}>🩰</span>
            <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--wine, #8B1D3B)' }}>
              Elite Tutoring System (ETS)
            </span>
          </div>

          <h3 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 700, color: '#1a1a1a' }}>
            {tField(batch.name, lang)}
          </h3>

          <div style={{ display: 'flex', justifyContent: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
            <span className="badge badge-wine" style={{ fontSize: 11 }}>{batch.level || 'Class'}</span>
            <span className="badge badge-muted" style={{ fontSize: 11 }}>
              {batch.days?.join(', ') || ''} {batch.startTime} - {batch.endTime}
            </span>
            {batch.tutor?.name && (
              <span className="badge badge-gold" style={{ fontSize: 11 }}>
                👨‍🏫 {tField(batch.tutor.name)}
              </span>
            )}
          </div>

          <div style={{
            background: '#fafafa',
            border: '2px dashed #e2e8f0',
            borderRadius: 16,
            padding: 20,
            display: 'inline-block',
            margin: '0 auto 16px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.04)'
          }}>
            <img 
              src={qrImageUrl} 
              alt="Classroom Check-in QR" 
              style={{ width: 220, height: 220, display: 'block', margin: '0 auto' }} 
            />
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 8, fontFamily: 'monospace' }}>
              BATCH ID: {batchId}
            </div>
          </div>

          <div style={{ 
            background: '#f8fafc', 
            borderRadius: 10, 
            padding: '12px 14px', 
            fontSize: 12, 
            color: '#475569', 
            lineHeight: 1.5,
            textAlign: 'left',
            marginBottom: 20
          }}>
            <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>🛡️</span>
              <span>{lang === 'zh' ? '導師簽到核實專用碼' : 'Faculty Verification Check-in QR'}</span>
            </div>
            {lang === 'zh' 
              ? '導師抵達教室後，請使用 ETS 手機應用程式中的「掃描教室二維碼」進行簽到。系統將自動驗證班級歸屬並記錄出席時間，作為每月薪資核算之憑據。' 
              : 'Tutors must scan this QR code via the ETS Mobile App upon arriving at the studio. The system verifies batch precision and logs verified teaching attendance for monthly payroll calculation.'}
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button 
              type="button" 
              className="btn btn-outline btn-sm" 
              onClick={handleCopy}
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            >
              {copied ? '✅ Copied' : '📋 Copy QR Data'}
            </button>
            <button 
              type="button" 
              className="btn btn-primary btn-sm" 
              onClick={handlePrint}
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'var(--wine, #8B1D3B)', borderColor: 'var(--wine, #8B1D3B)' }}
            >
              🖨️ {lang === 'zh' ? '列印簽到卡' : 'Print QR Card'}
            </button>
          </div>
        </div>

        <div style={{ background: '#f1f5f9', padding: '12px 24px', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e2e8f0' }}>
          <button type="button" className="btn btn-outline btn-sm" onClick={onClose} style={{ background: '#fff' }}>
            {lang === 'zh' ? '關閉' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
}

const FEE_BADGE = { Paid:'badge-green', Pending:'badge-orange', Overdue:'badge-red' };
const LEVEL_BADGE = { Beginner:'badge-blue', Intermediate:'badge-orange', Advanced:'badge-gold' };


export default function BatchDetail() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'zh' ? 'zh' : 'en';
  const { id }  = useParams();
  const navigate = useNavigate();
  const [batch, setBatch]         = useState(null);
  const [analytics, setAnalytics] = useState({ roster: [], heatmap: [] });
  const [loading, setLoad]    = useState(true);
  const [showModal, setModal] = useState(false);
  const [showEdit, setEdit]   = useState(false);
  const [showQrModal, setQrModal] = useState(false);
  const [isAdmin]             = useState(localStorage.getItem('ets_admin') === 'true');

  const fetchAll = useCallback(() => {
    setLoad(true);
    batchesAPI.getDeepDetails(id)
      .then(r => {
        setBatch(r.data.info);
        setAnalytics({
          roster: r.data.roster || [],
          removedStudents: r.data.removedStudents || [],
          heatmap: r.data.attendanceHeatmap || []
        });
      })
      .catch(console.error)
      .finally(() => setLoad(false));
  }, [id]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleApprove = async () => {
    if (!window.confirm('Are you sure you want to apply these tutor-proposed changes?')) return;
    try {
      await batchesAPI.approveEdit(id);
      fetchAll();
    } catch(err) { alert('Failed to approve changes.'); }
  };

  const handleReject = async () => {
    if (!window.confirm('Are you sure you want to discard these proposed changes?')) return;
    try {
      await batchesAPI.rejectEdit(id);
      fetchAll();
    } catch(err) { alert('Failed to reject changes.'); }
  };

  const handleToggleFee = async (s) => {
    const nextStatus = s.feeStatus === 'Paid' ? 'Pending' : s.feeStatus === 'Pending' ? 'Overdue' : 'Paid';
    try {
      await studentsAPI.updateStatus(s.id, nextStatus);
      fetchAll();
    } catch(err) {
      alert('Failed to update fee status.');
    }
  };

  const handleRemoveStudent = async (e, studentId, studentName) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to remove ${studentName} from this batch?`)) return;
    try {
      await batchesAPI.removeStudent(id, studentId);
      fetchAll();
    } catch(err) {
      alert(err.response?.data?.message || 'Failed to remove student');
    }
  };

  if (loading) return <Layout title="Batch"><div className="dash-loading">⏳ {t('batchDetail.loading')}</div></Layout>;
  if (!batch)  return <Layout title="Batch"><div className="dash-card"><div className="dash-empty"><div className="dash-empty-title">{t('batchDetail.notFound')}</div></div></div></Layout>;

  const students = analytics.roster || [];
  const paidCount = students.filter(s => s.feeStatus === 'Paid').length;
  const batchFee = batch.feeAmount || 1800;
  const totalExpected = students.length * batchFee;
  const totalCollected = paidCount * batchFee;


  return (
    <Layout title={`${tField(batch.name, lang)} — ${batch.level}`}>
      {showModal && <AddStudentModal batchObj={batch} onClose={() => setModal(false)} onAdded={fetchAll} />}
      {showEdit && <EditBatchModal batch={batch} onClose={() => setEdit(false)} onUpdated={fetchAll} />}
      {showQrModal && <ClassroomQrModal batch={batch} onClose={() => setQrModal(false)} />}

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24, flexWrap:'wrap', gap: 12 }}>
        <button className="btn btn-outline btn-sm" onClick={() => navigate(-1)}>{t('batchDetail.back', 'Back')}</button>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button 
            type="button"
            className="btn btn-outline btn-sm" 
            style={{ color: '#16a34a', borderColor: '#86efac', background: '#f0fdf4', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }} 
            onClick={() => setQrModal(true)}
            title="Classroom QR Code for Faculty Check-in"
          >
            <span>📷</span> {lang === 'zh' ? '班級簽到二維碼' : 'Classroom Check-in QR'}
          </button>
          <button className="btn btn-outline btn-sm" style={{ color:'var(--wine)', borderColor:'var(--wine)' }} onClick={() => setEdit(true)}>✏️ {t('batchDetail.editBatch')}</button>
        </div>
      </div>

      {isAdmin && batch.editStatus === 'Pending' && (
        <div className="dash-card mb-6" style={{ background:'#fff4e5', border:'1px solid #ffd580' }}>
          <div className="dash-card-header">
            <div className="dash-card-title" style={{ color:'#663c00' }}>⚠️ {t('batchDetail.pendingChanges')}</div>
            <div style={{ display:'flex', gap:10 }}>
              <button className="btn btn-sm btn-outline" style={{ background:'#fff', color:'#663c00' }} onClick={handleReject}>{t('batchDetail.discard')}</button>
              <button className="btn btn-sm btn-primary" style={{ background:'#663c00', border:'none' }} onClick={handleApprove}>{t('batchDetail.approveApply')}</button>
            </div>
          </div>
          <p className="text-sm" style={{ padding:'0 16px 16px', color:'#663c00' }}>{t('batchDetail.pendingSub')}</p>
        </div>
      )}

      {/* Financial Hub Summary */}
      <div className="dash-card mb-6" style={{ background:'linear-gradient(135deg, var(--black) 0%, #2a2a2a 100%)', color:'#fff', border:'none' }}>
        <div className="dash-card-header">
          <div className="dash-card-title" style={{ color:'#fff' }}>💎 {t('batchDetail.finHub')}</div>
          <span className="badge badge-gold" style={{ fontSize:10 }}>{t('batchDetail.premiumAnal')}</span>
        </div>
        <div style={{ padding:'0 24px 24px', display:'flex', gap:40, flexWrap:'wrap' }}>
          <div>
            <div style={{ fontSize:11, color:'rgba(255,255,255,0.5)', textTransform:'uppercase', letterSpacing:1 }}>{t('batchDetail.collectionProg')}</div>
            <div style={{ fontSize:28, fontWeight:700, fontFamily:"'Playfair Display', serif", color:'var(--wine-light)' }}>
              {paidCount} / {students.length} <span style={{ fontSize:14, fontWeight:400, color:'rgba(255,255,255,0.3)' }}>{t('batchDetail.studentsPaid')}</span>
            </div>
          </div>
          <div>
            <div style={{ fontSize:11, color:'rgba(255,255,255,0.5)', textTransform:'uppercase', letterSpacing:1 }}>{t('batchDetail.revenueMonth')}</div>
            <div style={{ fontSize:28, fontWeight:700, fontFamily:"'Playfair Display', serif" }}>
              ${totalCollected} <span style={{ fontSize:14, fontWeight:400, color:'rgba(255,255,255,0.3)' }}>/ ${totalExpected} HKD</span>
            </div>
          </div>
          <div style={{ marginLeft:'auto', textAlign:'right' }}>
            <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)' }}>{t('batchDetail.outstanding')}</div>
            <div style={{ fontSize:20, fontWeight:700, color:'#ff4d4f' }}>${totalExpected - totalCollected} HKD</div>
          </div>
        </div>
      </div>

      {/* Batch Info */}
      <div style={{ display:'flex', gap:16, marginBottom:28, flexWrap:'wrap' }}>
        <div className="dash-card" style={{ flex:1, minWidth:140, padding:'20px 20px', textAlign:'center' }}>
          <div style={{ display:'flex', justifyContent:'center', marginBottom: 10 }}>
             <div style={{ 
               width: 50, height: 50, borderRadius:'50%', 
               backgroundColor: 'var(--wine)',
               backgroundImage: batch.tutor?.photoUrl ? `url("${resolveImage(batch.tutor.photoUrl)}")` : 'none',
               backgroundSize: 'cover', backgroundPosition: 'center',
               display: 'flex', alignItems: 'center', justifyContent: 'center',
               color: '#fff', fontSize: 18, fontWeight: 700
             }}>
               {!batch.tutor?.photoUrl && tField(batch.tutor?.name)?.[0]}
             </div>
          </div>
          <div className="text-sm text-muted">{t('batchDetail.tutor')}</div>
          <div style={{ fontWeight:600, fontSize:15, marginTop:2 }}>{tField(batch.tutor?.name) || '—'}</div>
          {batch.tutor?.email && <div style={{ fontSize:12, color:'#888', marginTop:2 }}>{batch.tutor.email}</div>}
        </div>
        <div className="dash-card" style={{ flex:1, minWidth:140, padding:'20px 20px', textAlign:'center' }}>
          <div style={{ fontSize:28, marginBottom:6 }}>📚</div>
          <div className="text-sm text-muted">{t('batchDetail.level')}</div>
          <div style={{ marginTop:4 }}><span className={`badge ${LEVEL_BADGE[batch.level]}`}>{batch.level}</span></div>
        </div>
        <div className="dash-card" style={{ flex:1, minWidth:140, padding:'20px 20px', textAlign:'center' }}>
          <div style={{ fontSize:28, marginBottom:6 }}>👥</div>
          <div className="text-sm text-muted">{t('batchDetail.students')}</div>
          <div style={{ fontFamily:"'Playfair Display',serif", fontSize:28, fontWeight:700, color:'#c04060', marginTop:2 }}>{students.length}/{batch.maxStudents}</div>
        </div>
        <div className="dash-card" style={{ flex:1, minWidth:140, padding:'20px 20px', textAlign:'center' }}>
          <div style={{ fontSize:28, marginBottom:6 }}>🕐</div>
          <div className="text-sm text-muted">{t('batchDetail.schedule')}</div>
          <div style={{ fontWeight:700, fontSize:14, marginTop:2, color:'var(--wine)' }}>
            {batch.days?.join(', ') || '—'}
          </div>
          <div className="text-xs text-muted">
            {batch.startTime} - {batch.endTime}
          </div>
        </div>
      </div>

      {/* Attendance Heatmap */}
      {analytics.heatmap.length > 0 && (
        <div className="dash-card mb-6">
          <div className="dash-card-header">
            <div className="dash-card-title">{t('batchDetail.perfLast20')}</div>
            <div className="text-xs text-muted">{t('batchDetail.presentAbsent')}</div>
          </div>
          <div style={{ display:'flex', gap:6, overflowX:'auto', paddingBottom:8 }}>
            {analytics.heatmap.map((s, i) => {
              const total = s.present + s.absent;
              const isRescheduled = s.rescheduled > 0;
              const isHoliday = s.holiday > 0;
              const rate = total > 0 ? Math.round((s.present / total) * 100) : 0;
              
              if (total === 0 && (isHoliday || isRescheduled)) {
                const isResched = isRescheduled && !isHoliday;
                return (
                  <div key={i} style={{ minWidth:45, textAlign:'center' }}>
                    <div style={{ 
                      height:40, width:40, 
                      background: isResched ? '#0284c722' : '#64748b22',
                      border: `2px solid ${isResched ? '#0284c7' : '#64748b'}`,
                      borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:10, fontWeight:700, color: isResched ? '#0284c7' : '#64748b'
                    }}>
                      {isResched ? 'RES' : t('batchDetail.HOL', 'HOL')}
                    </div>
                    <div style={{ fontSize:9, marginTop:4, color:'#666', whiteSpace:'nowrap' }}>{s.date.split('-').slice(1).join('/')}</div>
                  </div>
                );
              }

              return (
                <div key={i} style={{ minWidth:45, textAlign:'center' }}>
                  <div style={{ 
                    height:40, 
                    width:40, 
                    background: rate >= 80 ? '#4caf8622' : rate >= 50 ? '#e07d2a22' : '#e0525222',
                    border: `2px solid ${rate >= 80 ? '#4caf86' : rate >= 50 ? '#e07d2a' : '#e05252'}`,
                    borderRadius:6,
                    display:'flex',
                    alignItems:'center',
                    justifyContent:'center',
                    fontSize:12,
                    fontWeight:700,
                    color: rate >= 80 ? '#4caf86' : rate >= 50 ? '#e07d2a' : '#e05252'
                  }}>
                    {rate}%
                  </div>
                  <div style={{ fontSize:9, marginTop:4, color:'#666', whiteSpace:'nowrap' }}>{s.date.split('-').slice(1).join('/')}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Students */}
      <div className="dash-card">
        <div className="dash-card-header">
          <div className="dash-card-title">{t('batchDetail.students', 'Students')} ({students.length})</div>
          <button className="btn btn-primary btn-sm" onClick={() => setModal(true)} disabled={students.length >= batch.maxStudents}>
            {students.length >= batch.maxStudents ? t('batchDetail.batchFull', 'Batch Full') : `+ ${t('batchDetail.addStudent', 'Add Student')}`}
          </button>
        </div>
        {analytics.roster.length === 0 ? (
          <div className="dash-empty">
            <div className="dash-empty-icon">👥</div>
            <div className="dash-empty-title">{t('batchDetail.noStudentsYet', 'No students yet')}</div>
            <p className="dash-empty-sub">{t('batchDetail.addStudentsDesc', 'Add students to this batch to begin tracking their progress.')}</p>
            <button className="btn btn-primary btn-sm" onClick={() => setModal(true)}>{t('batchDetail.addFirstStudent', 'Add First Student')}</button>
          </div>
        ) : (
          analytics.roster.map(s => {
            return (
              <div key={s.id} className="dash-list-item flex-wrap" style={{ cursor:'default' }}>
                <div className="dash-avatar" style={{ 
                  backgroundColor: 'rgba(123, 29, 58, 0.1)', 
                  color: 'var(--wine)', 
                  fontWeight: 700,
                  backgroundImage: s.photoUrl ? `url("${resolveImage(s.photoUrl)}")` : 'none',
                  backgroundSize: 'cover', backgroundPosition: 'center',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  overflow: 'hidden'
                }}>
                  {!s.photoUrl && s.name?.[0]?.toUpperCase()}
                </div>
                <div className="dash-list-item-main" style={{ cursor:'pointer' }} onClick={() => navigate(`/students/${s.id}`)}>
                  <div className="dash-list-item-name">{tField(s.name)} <span className="text-xs text-muted" style={{ marginLeft:8 }}>#{s.studentId}</span></div>
                  <div style={{ display:'flex', gap:10, marginTop:4, flexWrap:'wrap', alignItems:'center' }}>
                    <span 
                      className={`badge ${FEE_BADGE[s.feeStatus] || 'badge-muted'}`}
                      style={{ cursor:'pointer' }}
                      onClick={(e) => { e.stopPropagation(); handleToggleFee(s); }}
                      title="Click to toggle payment status"
                    >
                      {t('batchDetail.fee', 'Fee')}: {t(`batchDetail.feeStatus_${(s.feeStatus || '').toUpperCase()}`, s.feeStatus)}
                    </span>
                    <span className="text-sm text-muted">📊 {t('batchDetail.rate', 'Rate')}: <span style={{ color: s.attendanceRate >= 80 ? '#4caf86' : s.attendanceRate >= 50 ? '#e07d2a' : '#e05252', fontWeight:700 }}>{s.attendanceRate}%</span></span>
                    <span className="text-sm text-muted">👤 {t('batchDetail.parent', 'Parent')}: <span style={{ color:'var(--wine)', fontWeight:600 }}>{tField(s.parent.name)}</span></span>
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:16 }}>
                  <a href={`tel:${s.parent.mobile}`} className="btn btn-sm btn-outline" style={{ borderRadius:20, padding:'6px 12px', fontSize:11 }} onClick={e => e.stopPropagation()}>
                    📞 {t('batchDetail.callParent', 'Call Parent')}
                  </a>
                  {isAdmin && (
                    <button 
                      className="btn btn-sm btn-outline" 
                      style={{ borderRadius:20, padding:'6px 12px', fontSize:11, color:'var(--wine)', borderColor:'var(--wine)' }} 
                      onClick={e => handleRemoveStudent(e, s.id || s._id, tField(s.name))}
                    >
                      🗑️ {t('batchDetail.remove', 'Remove')}
                    </button>
                  )}
                  <span className="dash-list-arrow" style={{ cursor:'pointer' }} onClick={() => navigate(`/students/${s.id}`)}>›</span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Previously Enrolled Students Warning */}
      {analytics.removedStudents && analytics.removedStudents.length > 0 && (
        <div className="dash-card mb-6" style={{ background: '#fff9fa', border: '1px solid #f0e0e8' }}>
          <div className="dash-card-header" style={{ paddingBottom: 10 }}>
            <div className="dash-card-title" style={{ color: '#d97706', display: 'flex', alignItems: 'center', gap: 8 }}>
              ⚠️ {t('previouslyEnrolled', 'Previously Enrolled')} ({analytics.removedStudents.length})
            </div>
          </div>
          <div style={{ padding: '0 24px 20px', fontSize: 13, color: '#b45309', marginBottom: 12 }}>
            {t('removedStudentsWarning', 'These students were removed from this batch. Do not send payment notifications to them.')}
          </div>
          <div style={{ padding: '0 24px 24px' }}>
            {analytics.removedStudents.map((rs, i) => {
              const name = typeof rs.name === 'object' ? (rs.name.en || rs.name.zh || '') : (rs.name || '');
              const removedDate = rs.removedAt ? new Date(rs.removedAt) : null;
              const dateStr = removedDate 
                ? t('removedOn', { date: `${removedDate.getDate()}/${removedDate.getMonth()+1}/${removedDate.getFullYear()}` }) 
                : t('unknownDate', 'Unknown date');
              
              return (
                <div key={rs.studentId || i} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%', backgroundColor: '#fcd34d', color: '#b45309',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14
                  }}>
                    👤
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#92400e' }}>
                      {name} <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, marginLeft: 6 }}>({rs.etsStudentId})</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#b45309', opacity: 0.9 }}>
                      {dateStr}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Layout>
  );
}
